import React, { useEffect, useState } from "react";
import {
  Grid2,
  Typography,
  Paper,
  Divider,
  Box,
  Tooltip,
  IconButton,
  TextField,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/slices/toastSlice";
import { getGstDetails, updateGstDetails } from "../../../services/api/setting";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

const Gst = () => {
  const dispatch = useDispatch();
  const [gst, setGST] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newGst, setNewGst] = useState("");

  // Fetch GST details on mount
  useEffect(() => {
    const fetchGstDetails = async () => {
      try {
        const response = await getGstDetails();
        if (Array.isArray(response.data) && response.data.length > 0) {
          setGST(response.data[0]);
        }
      } catch (error) {
        dispatch(showToast({ message: error.message, severity: "error" }));
      }
    };

    fetchGstDetails();
  }, [dispatch]);

  // When edit is clicked
  const handleEditClick = () => {
    setIsEditing(true);
    setNewGst(gst?.gst_percentage || "");
  };

  // When cancel is clicked
  const handleCancelEdit = () => {
    setIsEditing(false);
    setNewGst("");
  };

  // Save edited GST
  const handleSaveEdit = async () => {
    if (!newGst || isNaN(newGst)) {
      dispatch(
        showToast({
          message: "Please enter a valid number",
          severity: "warning",
        })
      );
      return;
    }

    try {
      const payload = { gst_percentage: Number(newGst), id: gst.id };
      const response = await updateGstDetails(gst.id, payload);

      dispatch(showToast({ message: response.message, severity: "success" }));

      // Update local state
      setGST((prev) => ({ ...prev, gst_percentage: Number(newGst) }));
      setIsEditing(false);
    } catch (error) {
      dispatch(showToast({ message: error.message, severity: "error" }));
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        mb: 3,
        p: 2.5,
        borderRadius: 2,
        boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
      }}
    >
      <Typography variant="h5" gutterBottom>
        GST Setup
      </Typography>
      <Typography variant="body1" gutterBottom color="text.secondary">
        Set the GST in percentage. This will add GST in the tenant rent.
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Box display="flex" alignItems="center" gap={2}>
        <Grid2 container spacing={2} sx={{ pl: 4, pr: 4 }}>
          <Grid2 size={7}>
            <Typography variant="h6" color="textSecondary">
              GST
            </Typography>
          </Grid2>
          <Grid2 size={5}>
            {isEditing ? (
              <TextField
                type="number"
                value={newGst}
                onChange={(e) => setNewGst(e.target.value)}
                size="small"
                variant="outlined"
                sx={{ width: "100px" }}
                InputProps={{
                  endAdornment: <Typography>%</Typography>,
                }}
              />
            ) : (
              <Typography variant="h6">
                {gst ? `${gst.gst_percentage}%` : "Loading..."}
              </Typography>
            )}
          </Grid2>
        </Grid2>

        {isEditing ? (
          <>
            <Tooltip title="Save" arrow>
              <IconButton color="success" onClick={handleSaveEdit} size="small">
                <SaveOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel" arrow>
              <IconButton color="error" onClick={handleCancelEdit} size="small">
                <CloseOutlinedIcon />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Tooltip title="Edit" arrow>
            <IconButton onClick={handleEditClick} size="small">
              <EditOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
};

export default Gst;
